import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Countdown from 'react-countdown';
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Button, Col, Row } from 'react-bootstrap';
import { InputField } from '../../components/Common/FormikField/InputField';
import { email_required, password, string_required, GetSchema } from '../../js/Base/ValidationSchema';
import toastAlert from '../../js/Base/ToastAlert';
import OTPInput from './OTPInput';
import {
  getCaptcha,
  createAuthCodeAndSend,
  checkAuthCode
} from '../../api/authApi';
import { setupAutoLogout } from '../../js/Base/tokenManager';

export default function Login() {
  const [enableMFA, setEnableMFA] = useState(false);    // 是否開啟mfa(company)
  const [expiryTime, setExpiryTime] = useState(null);   // 設定逾時
  const [authCode, setAuthCode] = useState(0);          // 雙重六位驗證碼
  const [acctForAuth, setAcctForAuth] = useState(null); // 用來送 MFA 的 id
  const [captchaUrl, setCaptchaUrl] = useState(null);   // 圖片驗證碼
  const [mfaSending, setMfaSending] = useState(false);  // 寄送 MFA Email 狀態
  const [mfaExpired, setMfaExpired] = useState(false);  // 雙重驗證是否逾時

  const initialValues = {
    email: '',
    pwd: '',
    captcha: '',
  };

  const formSchema = GetSchema({
    email: email_required(),
    pwd: password(),
    captcha: string_required(),
  });

  const fetchCaptcha = async () => {
    try {
      const blob = await getCaptcha();
      const url = URL.createObjectURL(blob);
      setCaptchaUrl(url);
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('取得驗證碼錯誤');
    }
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  // 登入(無法使用api.post因為是不同個 session取不到captcha)
  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      const response = await fetch('/api/login_captcha/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const result = await response.json();
      const data = result.info
      if (!result.success) {
        fetchCaptcha();
        toastAlert.autoClose(true).showError('登入失敗');
        // setErrors({ general: data.error || '登入失敗' });
      } else {
        if (data.token) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user_id', data.id);
          localStorage.setItem('user_uuid', data.uuid);
          localStorage.setItem('user_name', data.name);
          localStorage.setItem('company_id', data.company_id);
          localStorage.setItem('company_uuid', data.company_uuid);
          localStorage.setItem('project_id', data.project_id);
          localStorage.setItem('project_uuid', data.project_uuid);
          localStorage.setItem('project_name', data.project_name);
          localStorage.setItem('user_email', data.email);

          setupAutoLogout();
        }

        // 設定雙重驗證
        setEnableMFA(data.enable_mfa);
        if (data.enable_mfa) {
          setAcctForAuth(data.uuid);
          // 15分鐘倒數
          setExpiryTime(Date.now() + 900000);
          // 自動寄送驗證碼
          sendMfaEmail(data.uuid);
        } else {
          window.location.href = '/HomePage';
        }
      }

    } catch (error) {
      fetchCaptcha();
      setErrors({ general: '登入失敗，請稍後再試' });
      toastAlert.autoClose(true).showError('登入失敗，發生錯誤!');
    } finally {
      setSubmitting(false);
    }
  };

  // 寄送 MFA 驗證碼
  const sendMfaEmail = async (acct_uuid) => {
    toastAlert.autoClose(true).showInfo('雙重驗證碼產生中...');
    setMfaSending(true);
    try {
      const data = await createAuthCodeAndSend(acct_uuid);

      if (!data.success) {
        toastAlert.autoClose(true).showError('寄送雙重驗證碼失敗');
        toLogin();
      } else {
        toastAlert.autoClose(true).showInfo('已寄送雙重驗證碼');
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('寄送雙重驗證碼失敗');
      toLogin();
    } finally {
      setMfaSending(false);
    }
  };

  const toLogin = () => {
    setTimeout(() => {
      document.location.assign(document.location.origin + '/');
    }, 3000);
  }

  const handleAuthCodeChange = (value) => {
    setAuthCode(value);
  };

  // 比對驗證碼
  const SendAuthCode = async () => {
    try {
      const data = await checkAuthCode({ acct_uuid: acctForAuth, auth_code: authCode });
  
      if (data.success) {
        window.location.href = '/HomePage';
      } else {
        toastAlert.autoClose(true).showError('雙重驗證失敗');
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('雙重驗證失敗');
    }
  };

  const input_style = {
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    paddingLeft: '0px',
    borderRadius: '0px',
    marginTop: '30px',
    fontWeight: 'bold',
    fontSize: '14px',
  };

  return (
    <div className="LoginManagement">
      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form className="">
            <InputField
              id="email"
              className="input-field"
              maxLength="50"
              inputName="email"
              placeholder="會員帳號"
              user-account
              style={input_style}
              isInvalid={errors.user_email && touched.user_email}
            />
            <InputField
              id="pwd"
              type="password"
              className="input-field"
              inputName="pwd"
              placeholder="會員密碼"
              style={input_style}
              isInvalid={errors.password && touched.password}
            />
            <div className="forget-password">
              <Link to="/forget-password" style={{ color: "#707070", fontSize: "12px", fontWeight: "bold" }}>
                忘記密碼
              </Link>
            </div>
            <div className="form-row">
              <div className="captcha-row">
                <InputField
                  id="captcha"
                  className="input-field"
                  maxLength="6"
                  inputName="captcha"
                  placeholder="驗證碼"
                  style={input_style}
                  showError={false}
                  isInvalid={errors.captcha_url && touched.captcha_url}
                />
                <img
                  src={captchaUrl}
                  alt="驗證碼"
                  className="captcha-img"
                  onClick={fetchCaptcha}
                />
              </div>
              <ErrorMessage name="captcha">
                {(msg) => <span className="text-danger" style={{ fontSize: "14px" }}>{msg}</span>}
              </ErrorMessage>
            </div>

            {enableMFA && (
              <>
                <Row style={{ marginTop: '16px' }}>
                  <Col sm={12} md={3} className="d-flex align-items-center">
                    <span
                      className="d-flex align-items-center"
                      style={{ fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                    >
                      雙重驗證碼
                    </span>
                  </Col>
                  <Col sm={12} md={9} style={{ marginTop: '6px' }}>
                    {' '}
                    <OTPInput length={6} onComplete={handleAuthCodeChange} />
                  </Col>
                  <Col
                    md={12}
                    className="d-flex justify-content-end align-items-center"
                    style={{
                      paddingTop: '14px',
                      fontSize: '12px',
                      color: '#5c6873',
                      gap: '8px',
                    }}
                  >
                    {/* <span>剩餘時間</span>
                    {expiryTime && <Countdown date={expiryTime} />} */}
                    {!mfaExpired && expiryTime ? (
                      <>
                        <span>剩餘時間</span>
                        <Countdown
                          date={expiryTime}
                          onComplete={() => setMfaExpired(true)}
                        />
                      </>
                    ) : (
                      <span className="error-text">雙重驗證已逾時，請重新登入</span>
                    )}
                  </Col>
                </Row>
              </>
            )}
            {/* <button type="submit" disabled={isSubmitting} style={{height: "36px"}}>
              {isSubmitting ? '登入中...' : '登入'}
            </button> */}
            <button
              type={enableMFA ? "button" : "submit"} // MFA-button, login-submit
              disabled={
                isSubmitting || (enableMFA ? !/^[0-9]{6}$/.test(authCode) : false) || mfaExpired
              }
              onClick={enableMFA ? SendAuthCode : undefined} // MFA 時呼叫 SendAuthCode
              style={{ height: "36px" }}
              className="btn-login btn-custom"
            >
              {enableMFA
                ? "送出"
                : isSubmitting
                ? "登入中..."
                : "登入"}
            </button>
            {errors.general && (
              <div className="error-text" style={{ marginBottom: 10 }}>
                {errors.general}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}
